import { useMemo, useState } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { ModelCard } from "../components/ModelCard";
import { useCatalog } from "../lib/useCatalog";
import { useHardware } from "../context/HardwareContext";
import { bestVerdict } from "../lib/compute";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Catalog() {
  const { data, isLoading } = useCatalog();
  const { hardware } = useHardware();
  const [q, setQ] = useState("");
  const [useCase, setUseCase] = useState("all");
  const [size, setSize] = useState("all");
  const [fit, setFit] = useState("all");

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.models.filter((m) => {
      if (q && !`${m.name} ${m.family} ${m.desc}`.toLowerCase().includes(q.toLowerCase())) return false;
      if (useCase !== "all" && !m.use_cases.includes(useCase)) return false;
      if (size === "s" && m.params > 7) return false;
      if (size === "m" && (m.params <= 7 || m.params > 20)) return false;
      if (size === "l" && m.params <= 20) return false;
      if (fit !== "all" && bestVerdict(m, hardware, data.quantizations) !== fit) return false;
      return true;
    });
  }, [data, q, useCase, size, fit, hardware]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-8">
      <div className="mb-6">
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Catalogue de modèles</h1>
        <p className="mt-1 text-muted-foreground">{data ? data.models.length : "…"} modèles open source, verdict basé sur votre matériel actuel.</p>
      </div>

      <div className="mb-6 flex flex-col gap-3 rounded-xl border border-[#1E2638] bg-[#0F141C] p-4 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un modèle…" data-testid="model-search-input" className="pl-9" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={useCase} onValueChange={setUseCase}>
            <SelectTrigger className="w-[150px]" data-testid="model-filter-usecase"><SelectValue placeholder="Usage" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les usages</SelectItem>
              {data?.use_cases.map((u) => <SelectItem key={u.id} value={u.id}>{u.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={size} onValueChange={setSize}>
            <SelectTrigger className="w-[140px]" data-testid="model-filter-size"><SelectValue placeholder="Taille" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes tailles</SelectItem>
              <SelectItem value="s">Petit (≤7B)</SelectItem>
              <SelectItem value="m">Moyen (7–20B)</SelectItem>
              <SelectItem value="l">Grand (&gt;20B)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={fit} onValueChange={setFit}>
            <SelectTrigger className="w-[150px]" data-testid="model-filter-fit"><SelectValue placeholder="Compatibilité" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toute compatibilité</SelectItem>
              <SelectItem value="can_run">Optimal</SelectItem>
              <SelectItem value="tight">Serré</SelectItem>
              <SelectItem value="no_go">Insuffisant</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Chargement…</p>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-[#1E2638] bg-[#0F141C] py-16 text-center">
          <SlidersHorizontal className="mb-2 h-8 w-8 text-muted-foreground" />
          <p className="text-muted-foreground">Aucun modèle ne correspond à ces filtres.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((m) => (
            <ModelCard key={m.id} model={m} quantizations={data.quantizations} />
          ))}
        </div>
      )}
    </div>
  );
}
